/**
 * Extract component name from error stack trace
 */
export function extractComponentFromErrorStack(stack: string | undefined): string | null {
  if (!stack) return null;
  
  const lines = stack.split('\n');
  for (const line of lines) {
    const match = line.match(/at\s+(\w+)(?:\.render|\.\w+)?\s*\(/);
    if (match && match[1] && !['Error', 'Object', 'Array', 'Promise', 'window'].includes(match[1])) {
      if (match[1][0] === match[1][0].toUpperCase()) {
        return match[1];
      }
    }
  }
  
  return null;
}

/**
 * Extract file path from stack trace
 */
export function extractFilePath(stack: string | undefined): string | null {
  if (!stack) return null;
  
  const lines = stack.split('\n');
  for (const line of lines) {
    const match = line.match(/([^\/\s]+\.(tsx?|jsx?)):(\d+):(\d+)/);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
}

/**
 * Extract detailed error information
 */
export function extractErrorDetails(error: any): Record<string, any> {
  const details: Record<string, any> = {};

  if (!error) return details;

  // Basic error properties
  if (error.name) details.name = error.name;
  if (error.message) details.message = error.message;
  if (error.stack) details.stack = error.stack;
  if (error.code) details.code = error.code;
  if (error.status) details.status = error.status;
  if (error.statusText) details.statusText = error.statusText;

  // For GraphQL errors
  if (error.graphQLErrors) {
    details.graphQLErrors = error.graphQLErrors.map((gqlError: any) => ({
      message: gqlError.message,
      locations: gqlError.locations,
      path: gqlError.path,
      extensions: gqlError.extensions
    }));
  }

  // For network errors
  if (error.networkError) {
    details.networkError = {
      message: error.networkError.message,
      statusCode: error.networkError.statusCode,
      result: error.networkError.result
    };
  }

  // For response errors
  if (error.response) {
    details.response = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: typeof error.response.data === 'string' 
        ? error.response.data.substring(0, 1000) 
        : error.response.data
    };
  }

  // For custom properties
  Object.keys(error).forEach(key => {
    if (!['name', 'message', 'stack', 'code', 'status', 'statusText', 'graphQLErrors', 'networkError', 'response'].includes(key)) {
      try {
        const value = error[key];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          details[key] = value;
        } else if (value && typeof value === 'object') {
          details[key] = JSON.stringify(value).substring(0, 500);
        }
      } catch (e) {
        // Ignore circular references
      }
    }
  });

  return details;
}

